import {
  getTranslation,
  translationStore
} from "./chunk-53GZFQK3.js";
import {
  isBrowser
} from "./chunk-IX6RFIQL.js";

// src/hooks/useDownload/useDownload.ts
import { useEffect, useState } from "react";

// src/hooks/useNotificationsStore/useNotificationsStore.ts
import { create } from "zustand";
var useNotificationsStore = create((set) => ({
  addNotification: (notification) => {
    set((state) => ({
      notifications: [...state.notifications, { id: Date.now(), ...notification }]
    }));
  },
  dismissNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((notification) => notification.id !== id)
    }));
  },
  notifications: []
}));

// src/hooks/useDownload/useDownload.ts
function useDownload() {
  const notifications = useNotificationsStore();
  const [state, setState] = useState(null);
  useEffect(() => {
    if (state) {
      const { blobType, data, filename } = state;
      const anchor = document.createElement("a");
      document.body.appendChild(anchor);
      const blob = new Blob([data], { type: blobType });
      const url = URL.createObjectURL(blob);
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
      anchor.remove();
      setState(null);
    }
  }, [state]);
  return async (filename, _data, options) => {
    try {
      const data = typeof _data === "function" ? await _data() : _data;
      if (typeof data !== "string" && !options?.blobType) {
        throw new Error("argument 'blobType' must be defined when download is called with a Blob object");
      }
      setState({ blobType: options?.blobType ?? "text/plain", data, filename });
    } catch (error) {
      const message = error instanceof Error ? error.message : "An unknown error occurred";
      notifications.addNotification({
        message,
        title: "Error",
        type: "error"
      });
    }
  };
}

// src/hooks/useEventCallback/useEventCallback.ts
import { useCallback, useRef } from "react";

// src/hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect.ts
import { useEffect as useEffect2, useLayoutEffect } from "react";
var useIsomorphicLayoutEffect = isBrowser() ? useLayoutEffect : useEffect2;

// src/hooks/useEventCallback/useEventCallback.ts
function useEventCallback(fn) {
  const ref = useRef(() => {
    throw new Error("Cannot call an event handler while rendering.");
  });
  useIsomorphicLayoutEffect(() => {
    ref.current = fn;
  }, [fn]);
  return useCallback((...args) => ref.current(...args), [ref]);
}

// src/hooks/useEventListener/useEventListener.ts
import { useEffect as useEffect3, useRef as useRef2 } from "react";
function useEventListener(eventName, handler, element, options) {
  const savedHandler = useRef2(handler);
  useIsomorphicLayoutEffect(() => {
    savedHandler.current = handler;
  }, [handler]);
  useEffect3(() => {
    const targetElement = element?.current ?? window;
    if (!(targetElement && targetElement.addEventListener)) return;
    const listener = (event) => savedHandler.current(event);
    targetElement.addEventListener(eventName, listener, options);
    return () => {
      targetElement.removeEventListener(eventName, listener, options);
    };
  }, [eventName, element, options]);
}

// src/hooks/useInterval/useInterval.ts
import { useEffect as useEffect4, useRef as useRef3 } from "react";
function useInterval(callback, delay) {
  const savedCallback = useRef3(callback);
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect4(() => {
    if (!delay && delay !== 0) {
      return;
    }
    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

// src/hooks/useMediaQuery/useMediaQuery.ts
import { useEffect as useEffect5, useState as useState2 } from "react";
function useMediaQuery(query) {
  const getMatches = (query2) => {
    if (isBrowser()) {
      return window.matchMedia(query2).matches;
    }
    return false;
  };
  const [matches, setMatches] = useState2(getMatches(query));
  function handleChange() {
    setMatches(getMatches(query));
  }
  useEffect5(() => {
    const matchMedia = window.matchMedia(query);
    handleChange();
    matchMedia.addEventListener("change", handleChange);
    return () => {
      matchMedia.removeEventListener("change", handleChange);
    };
  }, [query]);
  return matches;
}

// src/hooks/useOnClickOutside/useOnClickOutside.ts
import "react";
function useOnClickOutside(ref, handler, mouseEvent = "mousedown") {
  useEventListener(mouseEvent, (event) => {
    const el = ref.current;
    if (!el || el.contains(event.target)) {
      return;
    }
    handler(event);
  });
}

// src/hooks/useStorage/useStorage.ts
import { useCallback as useCallback2, useEffect as useEffect6, useState as useState3 } from "react";
function useStorage(key, initialValue, storageName, options = {}) {
  const { initializeWithValue = true } = options;
  const storage = window[storageName];
  const serializer = useCallback2(
    (value) => {
      if (options.serializer) {
        return options.serializer(value);
      }
      return JSON.stringify(value);
    },
    [options]
  );
  const deserializer = useCallback2(
    (value) => {
      if (options.deserializer) {
        return options.deserializer(value);
      } else if (value === "undefined") {
        return void 0;
      }
      const defaultValue = initialValue instanceof Function ? initialValue() : initialValue;
      let parsed;
      try {
        parsed = JSON.parse(value);
      } catch (err) {
        console.error(`Error parsing JSON: ${err.message}`);
        return defaultValue;
      }
      return parsed;
    },
    [options, initialValue]
  );
  const readValue = useCallback2(() => {
    const initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue;
    if (!isBrowser()) {
      return initialValueToUse;
    }
    const raw = storage.getItem(key);
    return raw ? deserializer(raw) : initialValueToUse;
  }, [initialValue, key, deserializer]);
  const [storedValue, setStoredValue] = useState3(() => {
    if (initializeWithValue) {
      return readValue();
    }
    return initialValue instanceof Function ? initialValue() : initialValue;
  });
  const setValue = useEventCallback((value) => {
    if (!isBrowser()) {
      console.warn(`Tried setting storage key \u201C${key}\u201D even though environment is not a client`);
    }
    try {
      const newValue = value instanceof Function ? value(readValue()) : value;
      storage.setItem(key, serializer(newValue));
      setStoredValue(newValue);
      window.dispatchEvent(new StorageEvent(storageName, { key }));
    } catch (error) {
      console.warn(`Error setting storage key \u201C${key}\u201D:`, error);
    }
  });
  useEffect6(() => {
    setStoredValue(readValue());
  }, [key]);
  const handleStorageChange = useCallback2(
    (event) => {
      if (event.key && event.key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue]
  );
  useEventListener("storage", handleStorageChange);
  useEventListener(storageName, handleStorageChange);
  return [storedValue, setValue];
}

// src/hooks/useStorage/useLocalStorage.ts
function useLocalStorage(key, initialValue, options = {}) {
  return useStorage(key, initialValue, "localStorage", options);
}

// src/hooks/useStorage/useSessionStorage.ts
function useSessionStorage(key, initialValue, options = {}) {
  return useStorage(key, initialValue, "sessionStorage", options);
}

// src/hooks/useTheme/useTheme.ts
import { useEffect as useEffect7, useState as useState4 } from "react";
var DEFAULT_THEME = "light";
var THEME_ATTRIBUTE = "data-mode";
var THEME_KEY = "theme";
var SYS_DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";
function useTheme() {
  const [theme, setTheme] = useState4(() => {
    if (!isBrowser()) {
      return DEFAULT_THEME;
    }
    const savedTheme = window.localStorage.getItem(THEME_KEY);
    let initialTheme;
    if (savedTheme === "dark" || savedTheme === "light") {
      initialTheme = savedTheme;
    } else {
      initialTheme = window.matchMedia(SYS_DARK_MEDIA_QUERY).matches ? "dark" : "light";
    }
    document.documentElement.setAttribute(THEME_ATTRIBUTE, initialTheme);
    return initialTheme;
  });
  useEffect7(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === THEME_ATTRIBUTE) {
          const updatedTheme = mutation.target.getAttribute(THEME_ATTRIBUTE);
          if (updatedTheme === "light" || updatedTheme === "dark") {
            window.localStorage.setItem(THEME_KEY, updatedTheme);
            setTheme(updatedTheme);
          } else {
            console.error(`Unexpected value for 'data-mode' attribute: ${updatedTheme}`);
          }
        }
      });
    });
    observer.observe(document.documentElement, {
      attributes: true
    });
    return () => observer.disconnect();
  }, []);
  const updateTheme = (theme2) => {
    document.documentElement.setAttribute(THEME_ATTRIBUTE, theme2);
  };
  return [theme, updateTheme];
}

// src/hooks/useTranslation/useTranslation.ts
import { useCallback as useCallback3 } from "react";
import { useStore } from "zustand";
function useTranslation(namespace) {
  const changeLanguage = useStore(translationStore, (store) => store.changeLanguage);
  const fallbackLanguage = useStore(translationStore, (store) => store.fallbackLanguage);
  const resolvedLanguage = useStore(translationStore, (store) => store.resolvedLanguage);
  const translations = useStore(translationStore, (store) => {
    if (namespace) {
      return store.translations[namespace];
    }
    return store.translations;
  });
  const t = useCallback3(
    (target, ...args) => {
      return getTranslation(target, { fallbackLanguage, resolvedLanguage, translations }, ...args);
    },
    [fallbackLanguage, resolvedLanguage, translations]
  );
  return { changeLanguage, resolvedLanguage, t };
}

// src/hooks/useWindowSize/useWindowSize.ts
import { useState as useState5 } from "react";
function useWindowSize() {
  const [windowSize, setWindowSize] = useState5({
    height: 0,
    width: 0
  });
  const handleSize = () => {
    setWindowSize({
      height: window.innerHeight,
      width: window.innerWidth
    });
  };
  useEventListener("resize", handleSize);
  useIsomorphicLayoutEffect(() => {
    handleSize();
  }, []);
  return windowSize;
}

export {
  useNotificationsStore,
  useDownload,
  useIsomorphicLayoutEffect,
  useEventCallback,
  useEventListener,
  useInterval,
  useMediaQuery,
  useOnClickOutside,
  useStorage,
  useLocalStorage,
  useSessionStorage,
  DEFAULT_THEME,
  THEME_ATTRIBUTE,
  THEME_KEY,
  SYS_DARK_MEDIA_QUERY,
  useTheme,
  useTranslation,
  useWindowSize
};
//# sourceMappingURL=chunk-AFNJZUOE.js.map